import {
  AppAlert,
  AppConfirmDeleteDialog,
  AppDataTable,
  AppSearchBox,
  AppSectionTitle,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { TaskSaveDialog } from '@opportunities/-components/TaskSaveDialog'
import { useDeleteOpportunityTask } from '@opportunities/-hooks/useDeleteOpportunityTask'
import { useUpdateOpportunityTask } from '@opportunities/-hooks/useUpdateOpportunityTask'
import { useMyTasks } from '@opportunities/tasks/-hooks/useMyTasks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { OpportunityTask } from 'backend/types'
import { toast } from 'sonner'
import { z } from 'zod'
import { AppExcelDownloadButton } from '@/components/AppExcelDownloadButton'
import { useTaskStatuses } from '@/shared-hooks/useTaskStatuses'
import { createTaskColumns } from './-components/columns'
import { useTasksDialogs } from './-hooks/useTasksDialogs'
import { useTasksFilter } from './-hooks/useTasksFilter'
import { useTasksTableState, VALID_PAGE_SIZES } from './-hooks/useTasksTableState'
import { useTasksUrlSync } from './-hooks/useTasksUrlSync'
import { excelColumns } from './-lib/excel-columns'

const tasksSearchSchema = z.object({
  search: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/opportunities/tasks/')({
  component: TasksPage,
  validateSearch: tasksSearchSchema,
  staticData: {
    breadcrumbLabel: '担当タスク一覧',
  },
})

function TasksPage() {
  // URL検索パラメータ同期
  const { search: searchParamsSearch } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { searchText, setSearchText } = useTasksUrlSync({ searchParamsSearch, navigate })

  // テーブル状態（Cookie永続化）
  const { pageSize, sortKey, sortDirection, handleSortChange, handlePageSizeChange } = useTasksTableState()

  // データ取得
  const { data: tasksData, isLoading: isTasksLoading, error: tasksError } = useMyTasks()
  const { data: statusesData, isLoading: isStatusesLoading } = useTaskStatuses()

  // フィルタリング
  const { filteredTasks } = useTasksFilter({ tasks: tasksData?.tasks, searchText })

  // ダイアログ状態
  const {
    editDialogOpen,
    setEditDialogOpen,
    editingTask,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deletingTask,
    handleEdit,
    handleDelete,
  } = useTasksDialogs()

  // Mutations
  const deleteTask = useDeleteOpportunityTask()
  const updateTask = useUpdateOpportunityTask()

  // ハンドラー
  const handleStatusChange = async (task: OpportunityTask, newStatusId: number) => {
    try {
      await updateTask.mutateAsync({
        opportunityId: task.opportunity_id,
        taskId: task.id,
        data: { opportunityTaskStatusId: newStatusId },
      })
      toast.success('ステータスを更新しました')
    } catch {
      toast.error('ステータスの更新に失敗しました')
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletingTask) return

    try {
      await deleteTask.mutateAsync({
        opportunityId: deletingTask.opportunity_id,
        taskId: deletingTask.id,
      })
      toast.success('タスクを削除しました')
      setDeleteDialogOpen(false)
    } catch {
      toast.error('タスクの削除に失敗しました')
    }
  }

  // テーブル表示用カラム定義
  const columns = createTaskColumns({
    statuses: statusesData?.statuses || [],
    onStatusChange: handleStatusChange,
    onEdit: handleEdit,
    onDelete: handleDelete,
  })

  // ローディング中
  if (isTasksLoading || isStatusesLoading) {
    return (
      <div className='flex h-full items-center justify-center'>
        <div>読み込み中...</div>
      </div>
    )
  }

  return (
    <div className='grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden'>
      {/* タイトルと検索欄（折りたたみ可能） */}
      <AppSectionTitle
        collapsible
        content={
          <div className='p-8 space-y-6'>
            {/* 説明 */}
            <AppAlert type='info'>担当しているタスクの一覧を表示します</AppAlert>

            {/* 検索ボックス、エクスポートボタン */}
            <div className='flex items-end gap-2'>
              <AppSearchBox
                label='担当タスクを検索'
                tooltipContent='タイトル、概要、オポチュニティ管理ID、オポチュニティ、オポステータス、SF案件名、期限、ステータス、更新者名、作成者名で検索できます'
                value={searchText}
                onChange={setSearchText}
                placeholder='タイトル、概要、オポチュニティ管理IDなどで絞り込み'
              />
              <AppExcelDownloadButton
                data={filteredTasks}
                columns={excelColumns}
                filename='タスク一覧'
                isFiltered={searchText !== ''}
                sortKey={sortKey}
                sortDirection={sortDirection}
                sortColumns={columns}
              />
            </div>
          </div>
        }
      >
        担当タスク一覧
      </AppSectionTitle>

      {/* テーブル */}
      <AppDataTable
        data={filteredTasks}
        columns={columns}
        isLoading={isTasksLoading}
        errorMessage={tasksError ? `タスクの取得に失敗しました: ${tasksError.message}` : undefined}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={VALID_PAGE_SIZES}
        emptyMessage='タスクがありません'
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        getRowKey={(row) => row.id}
        columnSelector={{ cookieKey: 'header-select-tasks' }}
      />

      {/* 編集ダイアログ */}
      {editingTask && (
        <TaskSaveDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          opportunityId={editingTask.opportunity_id}
          task={editingTask}
        />
      )}

      {/* 削除確認ダイアログ */}
      <AppConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        subtitle={deletingTask ? `タスク名: ${deletingTask.title}` : undefined}
        additionalMessages={['削除後は元に戻すことはできません。']}
        onConfirm={handleConfirmDelete}
        isPending={deleteTask.isPending}
      />
    </div>
  )
}
